import clsx from 'clsx';

import { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { Link } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';
import { ArrowTopRight, Left } from '../../../Common/Icon';
import { CategoryAndSubCategory } from '../../../../store/category';
import AnimatedList from '../../../Common/AnimatedList';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import ModalFullScreen from '../../../Common/ModalFullScreen';

import styles from './index.module.scss';

interface CategoryCardProps {
  category: CategoryAndSubCategory;
  isActive: boolean;
  onChange: (category: CategoryAndSubCategory | null) => void;
}

function CategoryCard({ category, isActive, onChange }: CategoryCardProps) {
  const size = useWindowSize();
  const {
    name, description, shortDescription, file, id, subCategories,
  } = category;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const [subCategoriesVisible, setSubCategoriesVisible] = useState(false);
  const [isDisplayOver, setIsDisplayOver] = useState(false);

  useEffect(() => {
    if (isActive && size.width > 768) {
      window.setTimeout(() => {
        setIsDisplayOver(true);
        const wrapperOffsetLeft = wrapperRef.current?.offsetLeft || 0;
        const wrapperClientWidth = wrapperRef.current?.parentElement?.clientWidth || 0;

        if (cardRef.current) {
          cardRef.current.style.left = `${-wrapperOffsetLeft}px`;
          cardRef.current.style.width = `${wrapperClientWidth}px`;

          window.setTimeout(() => {
            setSubCategoriesVisible(true);

            if (contentWrapperRef.current) {
              contentWrapperRef.current.style.gridTemplateColumns = `${
                (wrapperRef.current?.clientWidth || 0) - 28
              }px 1fr`;
            }
          }, 300);
        }
      }, 0);
    }
  }, [isActive, size]);

  useEffect(() => {
    if (!isActive) {
      if (cardRef.current) {
        cardRef.current.style.left = '0px';
        cardRef.current.style.width = '100%';
      }

      setSubCategoriesVisible(false);
      window.setTimeout(() => {
        setIsDisplayOver(false);
      }, 300);
    }
  }, [isActive]);

  const onClose = () => {
    onChange(null);
    setSubCategoriesVisible(false);
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div
        ref={cardRef}
        className={clsx(
          styles.card,
          isActive ? styles.selected : '',
          isDisplayOver || isActive ? styles.selectedZIndex : null,
        )}
        role="button"
        tabIndex={0}
        onClick={() => onChange(category)}
      >
        <div
          className={clsx(
            styles.contentWrapper,
            subCategoriesVisible && styles.contentWrapperGrid,
          )}
          ref={contentWrapperRef}
        >
          <div
            className={styles.content}
            style={{
              maxWidth: (wrapperRef.current?.clientWidth || 0) - 28 * 2,
            }}
          >
            <div className={styles.innerContentWrapper}>
              <div className={styles.iconWrapper}>
                <div className={styles.iconInnerWrapper}>
                  <img src={file?.location} className={styles.icon} alt="icon" />
                </div>
              </div>

              <div className={styles.name}>{name}</div>

              <p className={clsx(styles.description, 'truncate3')}>{shortDescription}</p>
            </div>

            <div className={styles.iconArrowWrapper}>
              <Link
                to={`/categories/${id}`}
                className={styles.btnMore}
                type="primary"
              >
                <ArrowTopRight className={styles.btnMoreIcon} />
                <span className={styles.btnMoreText}>More About {name}</span>
              </Link>
              <Left className={styles.iconArrow} />
            </div>
          </div>

          {subCategoriesVisible ? (
            <div className={styles.subcategoryWrapper}>
              <AnimatedList
                items={subCategories.map((subCategory) => (
                  <Link
                    key={crypto.randomUUID()}
                    to={`/categories/${id}/${subCategory.id}`}
                    className={styles.btnSubCategory}
                  >
                    {subCategory.header}
                  </Link>
                ))}
              />
            </div>
          ) : null}
        </div>

        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className={styles.btnClose}
        />
      </div>

      {size.width <= 768 ? (
        <ModalFullScreen isOpen={isActive} onCancel={onClose} theme="light">
          <div className={styles.modalContent}>
            <div className={clsx(styles.contentWrapper)}>
              <div className={styles.content}>
                <div className={styles.innerContentWrapper}>
                  <div className={styles.iconWrapper}>
                    <div className={styles.iconInnerWrapper}>
                      <img src={file?.location} className={styles.icon} alt="modal-icon" />
                    </div>
                  </div>

                  <div className={styles.name}>{name}</div>

                  <p className={styles.description}>{description}</p>
                </div>

                <div className={styles.iconArrowWrapper}>
                  <Link
                    to={`/categories/${id}`}
                    className={styles.btnMore}
                    type="primary"
                  >
                    <ArrowTopRight className={styles.btnMoreIcon} />
                    <span className={styles.btnMoreText}>
                      More About {name}
                    </span>
                  </Link>
                </div>
              </div>

              <div className={styles.subcategoryWrapper}>
                <AnimatedList
                  items={subCategories.map((subCategory) => (
                    <Link
                      key={subCategory.id}
                      to={`/categories/${id}/subCategories/${subCategory.id}`}
                      className={styles.btnSubCategory}
                    >
                      {subCategory.header}
                    </Link>
                  ))}
                />
              </div>
            </div>
          </div>
        </ModalFullScreen>
      ) : null}
    </div>
  );
}

export default CategoryCard;
